import styled from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, LabelUppercase } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import { useGetCompany } from '../../../hooks/use-hydra/admin';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Button from '../../elements/Button';
import useAuth from '../../../hooks/use-auth';
import React from 'react';
import { Table } from '../../Table/CompactTable';
import { createColumnHelper } from '@tanstack/react-table';
import Checkbox from '../../elements/Checkbox';
import { convertTimestampToLocalTime } from './AssignLearnersToMentorModal';
import { useMutation } from 'react-query';
import { updateMentorCohortAvailability } from '../../../api/admin';
import { toast } from 'react-toastify';
import { useBeforeUnload } from 'react-use';
import { Prompt } from 'react-router-dom';

dayjs.extend(utc);
dayjs.extend(timezone);

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 95vw;
  margin: 20px 0;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;

type TableData = {
  mentorProfileId: number;
  companyMentorId: number;
  officeHoursAvailability: Record<string, boolean>;
};

export default function UpdateMentorCohortAvailabilityModal({
  isOpen,
  onClose,
  companyId,
  cohortId,
}: {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  cohortId: number;
}) {
  const { getTokenSilently } = useAuth();
  const {
    data,
    refetch: refetchCompany,
    isFetching: isFetchingCompany,
  } = useGetCompany(companyId);

  const initialiseTableData = React.useCallback(() => {
    const cohort = data?.cohorts.find((c) => c.cycleId === cohortId);
    const cohortOfficeHourTimes = cohort?.officeHourTimes;

    // @ts-expect-error mixed types
    const tableData: TableData[] = (data?.mentors || [])
      .filter((m) => m.isCompanyMentor)
      .map((mentor) => ({
        mentorProfileId: mentor.id,
        companyMentorId: mentor.companyMentorId,
        officeHoursAvailability:
          cohortOfficeHourTimes?.reduce<Record<string, boolean>>(
            (acc, time) => {
              acc[new Date(time).toISOString()] = false;
              const timeKey = new Date(time).toISOString();
              const cohortMentor = data?.cohorts
                .find((c) => c.cycleId === cohortId)
                ?.mentors.find((m) => m.profileId === mentor.id);

              const officeHoursAvailability =
                cohortMentor?.officeHoursAvailability?.map((o) =>
                  new Date(o).toISOString()
                ) ?? [];

              acc[timeKey] = officeHoursAvailability.includes(timeKey) ?? false;
              return acc;
            },
            {}
          ) || {},
      }));
    return tableData;
  }, [data, cohortId]);

  const [tableData, setTableData] = React.useState<TableData[]>(
    initialiseTableData()
  );

  const hasUnsavedChanges = React.useMemo(() => {
    const cohort = data?.cohorts.find((c) => c.cycleId === cohortId);
    if (!cohort) return false;

    return tableData.some((row) => {
      const cohortMentor = cohort.mentors.find(
        (m) => m.profileId === row.mentorProfileId
      );
      const savedAvailabilities = new Set(
        (cohortMentor?.officeHoursAvailability ?? []).map((d) =>
          new Date(d).toISOString()
        )
      );
      const currentAvailabilities = new Set(
        Object.entries(row.officeHoursAvailability)
          .filter(([, v]) => v)
          .map(([k]) => k)
      );
      if (savedAvailabilities.size !== currentAvailabilities.size) return true;
      for (const time of savedAvailabilities) {
        if (!currentAvailabilities.has(time)) return true;
      }
      return false;
    });
  }, [tableData, data, cohortId]);

  useBeforeUnload(hasUnsavedChanges);

  const updateAvailability = React.useCallback(
    (companyMentorId: number, availability: string, isChecked: boolean) => {
      setTableData((prev) => {
        const newTableData = [...prev];
        return newTableData.map((row) => {
          if (row.companyMentorId === companyMentorId) {
            return {
              ...row,
              officeHoursAvailability: {
                ...row.officeHoursAvailability,
                [availability]: isChecked,
              },
            };
          }
          return row;
        });
      });
    },
    []
  );

  const columnHelper = createColumnHelper<TableData>();
  const columns = React.useMemo(() => {
    const base = [
      columnHelper.accessor('mentorProfileId', {
        header: () => 'Mentor',
        cell: (info) => {
          const mentor = data?.mentors.find(
            (m) => m.id === info.row.original.mentorProfileId
          );
          return <span>{mentor?.name}</span>;
        },
        meta: { styles: { minWidth: '180px', maxWidth: '220px' } },
        enableSorting: false,
      }),
    ];
    const officeHourCols = (
      data?.cohorts.find((c) => c.cycleId === cohortId)?.officeHourTimes ?? []
    ).map((date, idx) =>
      columnHelper.display({
        id: `officeHour-${idx}`,
        header: () => <span>{convertTimestampToLocalTime(date)}</span>,
        cell: (info) => {
          const iso = new Date(date).toISOString();
          const isChecked =
            info.row.original.officeHoursAvailability[iso] ?? false;

          return (
            <Checkbox
              isChecked={isChecked}
              onChange={() => {
                updateAvailability(
                  info.row.original.companyMentorId,
                  iso,
                  !info.row.original.officeHoursAvailability[iso]
                );
              }}
            />
          );
        },
        meta: {
          isCentered: true,
          styles: { minWidth: '180px', maxWidth: '180px', textAlign: 'center' },
        },
        enableSorting: false,
      })
    );
    return [...base, ...officeHourCols];
  }, [columnHelper, data, cohortId, updateAvailability]);

  const localTimezoneString = dayjs.tz.guess();

  const saveAvailabilitiesMutation = useMutation({
    mutationFn: async () => {
      const payload: Array<{
        companyMentorId: number | null;
        officeHourTimes: string[];
      }> = tableData.map((row) => {
        const officeHourTimes = Object.keys(row.officeHoursAvailability)
          .filter((key) => row.officeHoursAvailability[key] === true)
          .sort();
        return {
          companyMentorId: row.companyMentorId ?? null,
          officeHourTimes,
        };
      });

      const token = await getTokenSilently();

      await updateMentorCohortAvailability(token, {
        companyId,
        cohortId,
        availabilities: payload,
      });
    },
    onSuccess: async () => {
      await refetchCompany();
      toast.success('Mentor availability updated successfully');
      onClose();
    },
    onError: (error) => {
      console.error('Failed to update mentor availability:', error);
      toast.error('Something went wrong when updating mentor availability');
    },
  });

  React.useEffect(() => {
    if (isFetchingCompany || !isOpen) return;
    if (saveAvailabilitiesMutation.isLoading) return;
    setTableData(initialiseTableData());
  }, [
    data,
    initialiseTableData,
    isFetchingCompany,
    isOpen,
    saveAvailabilitiesMutation.isLoading,
  ]);

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (
        confirm(
          'You have unsaved changes. Are you sure you want to close this modal?'
        )
      ) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <Prompt
        when={hasUnsavedChanges}
        message="You have unsaved changes. Are you sure you want to close this modal?"
      />
      <Row>
        <Header>Update Mentors Cohort Availability</Header>
        <CloseButton onClick={handleClose} />
      </Row>

      <LabelUppercase>
        All times are in {localTimezoneString} timezone
      </LabelUppercase>
      <div style={{ margin: '32px 0' }}>
        <Table
          data={tableData}
          columns={columns}
          className="cohort-progress-table"
          initialPinning={{
            left: ['mentorProfileId'],
          }}
        />
      </div>

      <Row style={{ marginTop: 24 }}>
        <Button
          label="Update Mentor Availability"
          showIconBeforeLabel
          faIcon="fa-solid fa-paper-plane"
          type="primary"
          onClick={() => {
            saveAvailabilitiesMutation.mutate();
          }}
          isDisabled={
            saveAvailabilitiesMutation.isLoading || !hasUnsavedChanges
          }
          isLoading={saveAvailabilitiesMutation.isLoading}
        />
      </Row>
    </Wrapper>
  );
}
